import type { AxiosRequestConfig, AxiosResponse } from "axios";

import { netLogConfig } from "./net-log-config";

const REDACTED_HEADERS = ["authorization", "cookie", "set-cookie", "x-api-key"];

const requestStarts = new WeakMap<object, number>();

export function markRequestStart(config: AxiosRequestConfig): void {
  requestStarts.set(config, Date.now());
}

export function elapsedMs(config: AxiosRequestConfig | undefined): number | undefined {
  if (!config) {
    return undefined;
  }
  const start = requestStarts.get(config);
  return start === undefined ? undefined : Date.now() - start;
}

export function describeRequest(config: AxiosRequestConfig): string {
  const method = config.method ? config.method.toUpperCase() : "?";
  return `${method} ${fullUrl(config)}`;
}

export function describeDuration(config: AxiosRequestConfig | undefined): string {
  if (!netLogConfig.timing) {
    return "";
  }
  const elapsed = elapsedMs(config);
  return elapsed === undefined ? "" : `${elapsed}ms`;
}

export function summarizeHeaders(headers: unknown): string {
  const raw = toPlainObject(headers);
  if (!raw) {
    return "";
  }
  const safe = Object.entries(raw).reduce<Record<string, unknown>>((accumulator, [name, value]) => {
    accumulator[name] = REDACTED_HEADERS.includes(name.toLowerCase()) ? "[redacted]" : value;
    return accumulator;
  }, {});
  return summarizeBody(safe);
}

export function summarizeBody(data: unknown): string {
  if (data === undefined || data === null) {
    return "";
  }
  if (typeof data === "string") {
    return truncate(data, netLogConfig.maxBodyLength);
  }
  if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
    return `[binary ${formatBytes(data.byteLength)}]`;
  }
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    return "[FormData]";
  }
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    return `[Blob ${formatBytes(data.size)} ${data.type || "?"}]`;
  }
  try {
    return truncate(JSON.stringify(data, truncateFields) ?? "", netLogConfig.maxBodyLength);
  } catch {
    return "[unserializable]";
  }
}

function truncateFields(_key: string, value: unknown): unknown {
  return typeof value === "string" ? truncate(value, netLogConfig.maxFieldLength) : value;
}

function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max)}…(+${value.length - max} chars)`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes}B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)}KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function fullUrl(config: AxiosRequestConfig): string {
  const url = config.url ?? "";
  const baseUrl = config.baseURL ?? "";
  return !baseUrl || url.startsWith("http") ? url : `${baseUrl.replace(/\/$/, "")}/${url.replace(/^\//, "")}`;
}

function toPlainObject(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const candidate = value as { toJSON?: () => Record<string, unknown> };
  return typeof candidate.toJSON === "function" ? candidate.toJSON() : (value as Record<string, unknown>);
}

export function responseSize(response: AxiosResponse | undefined): string {
  const data = response?.data;
  if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
    return formatBytes(data.byteLength);
  }
  if (typeof data === "string") {
    return formatBytes(data.length);
  }
  return "";
}
